import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { useAppContext } from '../context/AppContext';
import { videoUpscaleApi } from '../services/api';
import VideoUploader, { UploadedVideo } from '../components/VideoUploader';
import VideoPlayer from '../components/VideoPlayer';
import GenerationHistory from '../components/GenerationHistory';

// Utility function to handle both base64 and presigned URL formats
const processVideoDataUri = (data_uri: string): string => {
  // Check if it's already a URL (presigned URL)
  if (data_uri.startsWith('http://') || data_uri.startsWith('https://')) {
    return data_uri;
  }
  
  // Handle base64 format
  const clean = data_uri.startsWith('data:') ? data_uri.split(',')[1] : data_uri;
  return `data:video/mp4;base64,${clean}`;
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const VideoUpscaleGenerator: React.FC = () => {
  const { state, actions } = useAppContext();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedQueue] = useState<string>('seedvr2-upscale');
  const [uploadedVideo, setUploadedVideo] = useState<UploadedVideo | null>(null);
  const [targetResolution, setTargetResolution] = useState<string>('720');

  const UPSCALE_QUEUES = ['seedvr2-upscale'];

  const RESOLUTION_OPTIONS = [
    { value: '720', label: '720p' },
    { value: '960', label: '960p (may OOM)' },
    { value: '1080', label: '1080p (may OOM)' },
    { value: '1440', label: '1440p (may OOM)' },
  ];

  const { errorMessage, generationProgress } = state;
  const { isGenerating, generatedVideo, upscaleVideo } = state;

  useEffect(() => {
    return () => {
      // cleanup on unmount
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  // Auto-handle video from other pages
  useEffect(() => {
    if (upscaleVideo && !uploadedVideo && !isGenerating) {
      // Convert the video URL to a File object for upload
      fetch(upscaleVideo)
        .then(response => response.blob())
        .then(blob => {
          const file = new File([blob], 'generated-video.mp4', { type: 'video/mp4' });
          setUploadedVideo({ file, preview: upscaleVideo });
          // Clear the upscale video from state to avoid repeated processing
          actions.setUpscaleVideo('');
        })
        .catch(error => {
          console.error('Error converting video for upscale:', error);
          setErrorMessage('Failed to prepare video for upscaling');
        });
    }
  }, [upscaleVideo, uploadedVideo, isGenerating, actions]);

  // Auto-trigger upscaling when video is uploaded from other pages
  useEffect(() => {
    if (uploadedVideo && upscaleVideo === '' && !isGenerating && !generatedVideo) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        handleUpscale();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [uploadedVideo, upscaleVideo, isGenerating, generatedVideo]);

  const handleVideoUpload = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedVideo({ file, preview: String(reader.result) });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => setUploadedVideo(null);
  const setErrorMessage = (message: string) => actions.setError(message);

  const handleReset = () => {
    actions.prepareNewGenerationNoHistory();
    setErrorMessage('');
    setStatusMessage('');
  };

  const clearPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressPolling = (taskId: string) => {
    setStatusMessage('Starting video upscaling... this usually takes ~2 minutes');
    const pollInterval = 10000;
    const maxPollTime = 900000; // 15 minutes for upscaling
    actions.updateGenerationProgress(0);
    pollStartTimeRef.current = Date.now();
    clearPolling();

    let currentIntervalId: any = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollForStatus = async () => {
      const currentTime = Date.now();
      const startTime = pollStartTimeRef.current ?? currentTime;
      const elapsedTime = currentTime - startTime;
      if (elapsedTime > maxPollTime) {
        if (currentIntervalId) {
          clearInterval(currentIntervalId);
          progressIntervalRef.current = null;
        }
        setErrorMessage('Task timeout - upscaling took too long');
        actions.prepareNewGenerationNoHistory();
        return;
      }
      try {
        const {
          status,
          progress: progressVal,
          statusMessages,
          queue_position,
        } = (await videoUpscaleApi.checkStatus(taskId, selectedQueue)) as any;
        const progress = typeof progressVal === 'number' ? progressVal : undefined;
        consecutiveErrors = 0;
        if (typeof progress === 'number') actions.updateGenerationProgress(progress * 100);
        if (statusMessages)
          setStatusMessage(statusMessages.detail_message || statusMessages.message || '');
        else if (typeof progress === 'number')
          setStatusMessage(`Processing: ${Math.round(progress * 100)}%`);

        if (status && status.toLowerCase() === 'in_queue') {
          const tagline = queue_position ? `${queue_position} ahead of you.` : "You're next.";
          setStatusMessage(`One moment. We'll start upscaling shortly. ${tagline}`);
          return;
        }

        if (status && status.toLowerCase() === 'cancelled') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video upscaling cancelled');
          setErrorMessage('Task was cancelled');
          actions.prepareNewGenerationNoHistory();
          return;
        }

        if (status && status.toLowerCase() === 'failed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          const failureReason = statusMessages?.message || statusMessages?.detail_message || 'Unknown error';
          setStatusMessage(`Video upscaling failed: ${failureReason}`);
          setErrorMessage(`Task failed: ${failureReason}. This may be due to video format, resolution limits, or processing constraints. Please try with a different video or lower resolution.`);
          actions.prepareNewGenerationNoHistory();
          return;
        }

        if (status && status.toLowerCase() === 'completed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video upscaling completed! Fetching result ...');

          const { video } = await videoUpscaleApi.getResult(taskId, selectedQueue);
          const data_uri = video?.data_uri;

          if (video && data_uri) {
            const dataUriStr = data_uri as string;
            if (typeof dataUriStr !== 'string' || dataUriStr.length === 0) {
              setErrorMessage('Invalid video data received');
              actions.prepareNewGeneration();
              return;
            }

            setStatusMessage('Video upscaling completed.');
            const videoUrl = processVideoDataUri(dataUriStr);
            actions.completeGeneration(videoUrl);
          } else {
            setErrorMessage('Video upscaled but no data was provided');
            actions.prepareNewGeneration();
          }
        }
      } catch (err: any) {
        if (err.response && (err.response.status === 404 || err.response.status === 422)) {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setErrorMessage(`Task error: ${err.response?.data?.detail || 'Unknown error'}`);
          actions.prepareNewGenerationNoHistory();
        } else {
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            if (currentIntervalId) {
              clearInterval(currentIntervalId);
              progressIntervalRef.current = null;
            }
            setErrorMessage(`Too many polling errors: ${err.message || 'Unknown error'}`);
            actions.prepareNewGenerationNoHistory();
          } else {
            setErrorMessage(
              `Polling error: ${err.message || 'Unknown error'} - Will retry... (${consecutiveErrors}/${maxConsecutiveErrors})`,
            );
          }
        }
      }
    };

    pollForStatus();
    const interval = setInterval(pollForStatus, pollInterval);
    currentIntervalId = interval;
    progressIntervalRef.current = interval;
    return interval;
  };

  const handleUpscale = async () => {
    if (!uploadedVideo) {
      alert('Please upload a video');
      return;
    }
    setErrorMessage('');
    setStatusMessage('Submitting upscale request...');
    try {
      const { request_id } = await videoUpscaleApi.upscaleVideo(
        uploadedVideo.file,
        selectedQueue,
        targetResolution,
      );
      actions.startGeneration(request_id);
      startProgressPolling(request_id);
    } catch (error: any) {
      let errorDetail = 'Unknown error';
      if (error.response?.data?.detail) errorDetail = error.response.data.detail;
      else if (error.message) errorDetail = error.message;
      setErrorMessage(`Failed to start video upscaling: ${errorDetail}`);
      alert(`Error: ${errorDetail}`);
      actions.cancelGeneration();
    }
  };

  return (
    <Grid container spacing={3} sx={{ height: 'calc(100vh - 120px)' }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StyledPaper elevation={3} sx={{ mb: 3, flex: '1 1 auto' }}>
          <Typography variant="h5" gutterBottom>
            Video Upload
          </Typography>
          <Box sx={{ mb: 3, flex: '0 0 auto' }}>
            <VideoUploader
              uploadedVideo={uploadedVideo}
              onUpload={handleVideoUpload}
              onRemove={handleRemoveVideo}
            />
          </Box>
          <Typography variant="h5" gutterBottom>
            Upscale Settings
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel id="upscale-queue-label">Queue</InputLabel>
              <Select
                labelId="upscale-queue-label"
                id="upscale-queue"
                value={selectedQueue}
                label="Queue"
                disabled={true} // Only one queue option
              >
                {UPSCALE_QUEUES.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 200 }}>
              <InputLabel id="target-resolution-label">Target Resolution</InputLabel>
              <Select
                labelId="target-resolution-label"
                id="target-resolution"
                value={targetResolution}
                label="Target Resolution"
                disabled={isGenerating}
                onChange={(e) => setTargetResolution(e.target.value as string)}
              >
                {RESOLUTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
            {!generatedVideo ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={!uploadedVideo || isGenerating}
                  onClick={handleUpscale}
                  startIcon={<VideoLibraryIcon />}
                  fullWidth
                >
                  Upscale Video
                </Button>
                {isGenerating && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    onClick={() => {
                      if (state.generationTaskId) {
                        clearPolling();
                        videoUpscaleApi
                          .cancelTask(state.generationTaskId, selectedQueue)
                          .then(() => {
                            actions.prepareNewGenerationNoHistory();
                            setStatusMessage('Video upscaling cancelled');
                          })
                          .catch((err: any) => {
                            actions.prepareNewGenerationNoHistory();
                            setErrorMessage(
                              'Error cancelling task: ' + (err.message || 'Unknown error'),
                            );
                          });
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  clearPolling();
                  actions.prepareNewGenerationNoHistory();
                  handleUpscale();
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                New Upscale
              </Button>
            )}
          </Box>
        </StyledPaper>
        <Box sx={{ flex: '0 0 auto', height: '250px' }}>
          <GenerationHistory />
        </Box>
      </Grid>
      <Grid item xs={12} md={6} sx={{ height: '100%' }}>
        <StyledPaper
          elevation={3}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Typography variant="h5" gutterBottom>
            Upscaled Video - Usually takes around 2 minutes
          </Typography>
          {isGenerating ? (
            <Box sx={{ width: '100%', mt: 4, position: 'relative' }}>
              <Typography variant="body1" gutterBottom>
                {statusMessage || 'Upscaling video... This may take around 2 minutes.'}
              </Typography>
              {errorMessage ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    borderLeft: '4px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Error Occurred
                  </Typography>
                  <Typography variant="body1">{errorMessage}</Typography>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={handleReset}
                  >
                    Reset and Try Again
                  </Button>
                </Paper>
              ) : (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={generationProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mb: 1,
                      '& .MuiLinearProgress-bar': { animation: 'pulse 1.5s infinite ease-in-out' },
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="right"
                  >{`${Math.round(generationProgress)}%`}</Typography>
                </>
              )}
              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  height: '300px',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <Typography variant="h6" color="white" align="center">
                  {statusMessage || 'Processing your video...'}
                </Typography>
              </Box>
            </Box>
          ) : generatedVideo ? (
            <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <VideoPlayer videoUrl={generatedVideo} />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body1">
                No video upscaled yet. Start by uploading a video and selecting target resolution.
              </Typography>
            </Box>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default VideoUpscaleGenerator;
